/**
 * ========================================
 * NOTIFICATION SERVICE
 * ========================================
 * API calls for the notification bell dropdown.
 * Endpoints: /api/v1/notifications/*
 */

import { apiClient } from './client';
import { handleAPIError } from './error-handler';
import type { AppNotification } from '@/types/notification';

interface BackendEnvelope<T> {
  status: string;
  status_code?: number;
  message: string;
  data: T;
  summary?: { total: number; page: number; page_size: number };
}

export interface NotificationListResult {
  items: AppNotification[];
  total: number;
}

class NotificationService {
  private baseUrl = '/api/v1/notifications';

  async list(params?: { unread_only?: boolean; page?: number; page_size?: number }): Promise<NotificationListResult> {
    try {
      const response = await apiClient.get<BackendEnvelope<AppNotification[]>>(this.baseUrl, { params });
      return { items: response.data.data, total: response.data.summary?.total ?? response.data.data.length };
    } catch (error) {
      handleAPIError(error);
    }
  }

  async unreadCount(): Promise<number> {
    try {
      const response = await apiClient.get<BackendEnvelope<{ count: number }>>(`${this.baseUrl}/unread-count`);
      return response.data.data.count;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async markRead(id: string): Promise<AppNotification> {
    try {
      const response = await apiClient.patch<BackendEnvelope<AppNotification>>(`${this.baseUrl}/${id}/read`);
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }

  async markAllRead(): Promise<number> {
    try {
      const response = await apiClient.post<BackendEnvelope<{ count: number }>>(`${this.baseUrl}/read-all`);
      return response.data.data.count;
    } catch (error) {
      handleAPIError(error);
    }
  }
}

export const notificationService = new NotificationService();
export default notificationService;
